/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback } from "react";
import { Box, Button, useTheme, useMediaQuery } from "@mui/material";
import { LearningCarouselHeader } from "./learning-carousel-header";
import { LearningCarousel } from "./learning-carousel";
import {
  SORT_ALPHABETICAL_ASCENDING,
  SORT_ALPHABETICAL_DESCENDING,
  SORT_DURATION_ASCENDING,
  SORT_DURATION_DESCENDING,
  SORT_PRICE_ASCENDING,
  SORT_PRICE_DESCENDING,
  SORT_DEFAULT,
} from "utils/constants";
import { useTranslation } from "react-i18next";
import { useFocusStyles } from "hooks/focusBorder";
import { isNumber } from "lodash";
import { Tabs, TabPanel, Select } from "components";
import portalSettingsManager from "utils/portalSettingsManager";
import styles from "./learning-carousel.module.css";
import { useAppDispatch } from "store";
import { useNavigate } from "react-router-dom";
import { CategoryCode, LearningSection, SortType, TabModel } from "utils/types";

export const MyLearningCarouselContainer = (props: any): React.ReactElement => {
  const isMyLearningTileView: boolean | undefined =
    portalSettingsManager.application?.common?.showMyLearningSectionAsCarousal;
  const component = !isMyLearningTileView ? (
    <TileView title={props.title} {...props} />
  ) : (
    <CarouselView title={props.title} {...props} />
  );

  return component;
};

const configuredCategories = [
  CategoryCode.Required,
  CategoryCode.MyInterest,
  CategoryCode.Channel,
  CategoryCode.ParentPathway,
];

const defaultCounts = new Map<CategoryCode, number>(configuredCategories.map((c) => [c, 0]));
const defaultSorts = new Map<CategoryCode, string>(
  configuredCategories.map((c) => [c, SORT_DEFAULT])
);
const defaultLoadingStates = new Map<CategoryCode, boolean>(
  configuredCategories.map((c) => [c, true])
);

const CarouselView = (props: any): React.ReactElement => {
  const [contentCount, setContentCount] = useState<number | undefined>();
  const [sectionSorts, setSectionSorts] = useState<Map<CategoryCode, string>>(defaultSorts);
  const [sectionCounts, setSectionCounts] = useState<Map<CategoryCode, number>>(defaultCounts);
  const [sectionLoadingState, setSectionLoadingState] =
    useState<Map<CategoryCode, boolean>>(defaultLoadingStates);

  const { t } = useTranslation();
  const defaultLearningSections = [
    { code: CategoryCode.Required, title: t("learningsection_my_enrollments_title") },
    { code: CategoryCode.MyInterest, title: t("learningsection_my_playlists_title") },
    { code: CategoryCode.Channel, title: t("learningsection_my_channels_title") },
    { code: CategoryCode.ParentPathway, title: t("learningsection_my_parentpathway_title") },
  ];
  const [learningSections, __] = useState<LearningSection[]>(
    defaultLearningSections.filter((ls) => configuredCategories.includes(ls.code))
  );
  const [activeSection, setActiveSection] = useState<LearningSection>(
    defaultLearningSections.find((l) => l.code == CategoryCode.Required)
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallerMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const handleSort = (sort: SortType) => {
    setSectionSorts((prevState) => {
      prevState.set(activeSection.code, sort);
      return new Map<CategoryCode, string>(prevState);
    });
  };

  const lazyLoadMyLearningCarousal =
    portalSettingsManager?.application?.common?.lazyLoadMyLearningCarousal;

  const updateListCallback = useCallback(
    (__: any, contentCount: number | undefined, code: CategoryCode): void => {
      setSectionCounts((prevState) => {
        prevState.set(code, isNumber(contentCount) ? contentCount : 0);
        return new Map<CategoryCode, number>(prevState);
      });
    },
    []
  );

  useEffect(() => {
    if (!lazyLoadMyLearningCarousal) {
      const firstSectionWithEntries = Array.from(sectionCounts).find((c) => c[1] > 0);
      if (firstSectionWithEntries) {
        const newActiveSection = learningSections.find((s) => s.code == firstSectionWithEntries[0]);
        setActiveSection(newActiveSection);
      }
    }
  }, [sectionCounts, lazyLoadMyLearningCarousal]);

  useEffect(() => {
    const total = Array.from(sectionCounts.values()).reduce(
      (count, value) => Number(count) + Number(value),
      0
    );
    setContentCount(Number(total));
  }, [sectionCounts]);

  const getTabIndexByCategoryCode = (code: CategoryCode) => {
    return learningSections.findIndex((l) => l.code == code);
  };

  const getTabs = () => {
    const tabs: TabModel<number>[] = [];
    learningSections.forEach((learningSection) => {
      if (!lazyLoadMyLearningCarousal && sectionCounts.get(learningSection.code) == 0) return;

      tabs.push({
        label: getTabTitle(learningSection),
        value: getTabIndexByCategoryCode(learningSection.code),
        tabId: learningSection.code,
        tabPath: learningSection.code,
      });
    });

    return tabs;
  };

  const handleViewAll = (): void => {
    dispatch({
      type: "search/setSearchFilters",
      payload: {
        sort: SORT_ALPHABETICAL_ASCENDING,
        text: "",
        code:
          activeSection.code === CategoryCode.Channel ? CategoryCode.Required : activeSection.code,
      },
    });
    navigate(`/catalog/${activeSection.code}`);
    window.scrollTo(0, 0);
  };

  const handleCustomButtonClick = (code: string): void => {
    dispatch({
      type: "search/setSearchFilters",
      payload: {
        sort: SORT_DEFAULT,
        text: "",
        code,
      },
    });
    navigate(`/catalog/${code}`);
    window.scrollTo(0, 0);
  };

  const handleTabChange = (event: React.ChangeEvent<unknown>, newValue: any) => {
    setActiveSection(learningSections[newValue]);
  };

  function handleOnLoadingChanged(code: CategoryCode, state: boolean) {
    setSectionLoadingState((prevState) => {
      prevState.set(code, state);
      return new Map<CategoryCode, boolean>(prevState);
    });
  }

  const getTabTitle = (section: LearningSection): string => {
    const count = sectionCounts.get(section.code);

    if (!lazyLoadMyLearningCarousal && count && count > 0) {
      return `${section.title} (${count})`;
    }

    return section.title;
  };

  return (
    <>
      <Box marginTop={{ xs: "10px", sm: "25px" }}>
        <LearningCarouselHeader
          title={props.title}
          showViewAll={false}
          handleViewAll={props.handleViewAll}
          showHeaderCount
          showHeaderWithZeroCount
          contentCount={lazyLoadMyLearningCarousal ? 0 : contentCount}
        />
      </Box>
      <Box
        display={{ xs: "none", sm: "flex" }}
        flexDirection={`${isSmallerMobile ? "column" : "row"}`}
        margin={`${isSmallerMobile ? "0 -10px" : "0"}`}
        style={{
          justifyContent: "space-between",
        }}
      >
        <Box
          style={{ width: `${isSmallerMobile ? "100%" : isMobile ? "calc(100% - 215px)" : ""}` }}
        >
          <Tabs
            value={getTabIndexByCategoryCode(activeSection.code)}
            aria-label={t("aria_label_my_learning_tabs")}
            tabs={getTabs()}
            onChange={handleTabChange}
            height={40}
          />
        </Box>
        {sectionCounts.get(activeSection.code) > 0 ? (
          <Box
            style={{ display: "flex", alignItems: "flex-end" }}
            marginTop={`${isSmallerMobile ? "10px" : "0"}`}
            justifyContent={`${isSmallerMobile ? "flex-end" : "flex-start"}`}
          >
            <Box display={{ xs: "none", sm: "block" }}>
              <Box display="flex" alignItems="center">
                <Box marginRight={{ xs: 0, sm: "10px" }}>
                  <label id="sort-by" style={{ fontSize: "10px" }}>
                    {t("sort_by")}
                  </label>
                </Box>
                <Select
                  title={t("sort_by")}
                  arialabel={activeSection.code + " section "}
                  aria-labelledby={"sort-by sort-by-select"}
                  id={"sort-by-select"}
                  buttonLabelId={"sort-by"}
                  options={[
                    { title: t("sort_by_last_progress"), id: SORT_DEFAULT },
                    { title: t("sort_by_name_asc"), id: SORT_ALPHABETICAL_ASCENDING },
                    { title: t("sort_by_name_desc"), id: SORT_ALPHABETICAL_DESCENDING },
                    { title: t("sort_by_duration_asc"), id: SORT_DURATION_ASCENDING },
                    { title: t("sort_by_duration_desc"), id: SORT_DURATION_DESCENDING },
                    { title: t("sort_by_price_asc"), id: SORT_PRICE_ASCENDING },
                    { title: t("sort_by_price_desc"), id: SORT_PRICE_DESCENDING },
                  ]}
                  value={sectionSorts.get(activeSection.code)}
                  onChange={handleSort}
                  aria-selected={activeSection.title === t("sort_by")}
                />
              </Box>
            </Box>
            {!isSmallerMobile ? (
              <CustomButton
                title={activeSection.title}
                handleClick={handleViewAll}
                label={t("view_all_button_text")}
              />
            ) : (
              <></>
            )}
          </Box>
        ) : (
          <></>
        )}
      </Box>
      <Box style={{ minHeight: "365px" }} display={{ xs: "none", sm: "block" }}>
        {learningSections.map((section: LearningSection, index: number) => (
          <TabPanel
            key={index}
            value={getTabIndexByCategoryCode(activeSection.code)}
            index={getTabIndexByCategoryCode(section.code)}
            persistInDom
            style={{ padding: "10px 0 0 0" }}
            loading={sectionLoadingState.get(section.code)}
          >
            <>
              <LearningCarousel
                loadData={!lazyLoadMyLearningCarousal || section.code === activeSection.code}
                onLoadingDataChanged={(l) => handleOnLoadingChanged(section.code, l)}
                {...section}
                selectTileHandler={props.selectTileHandler}
                setOpenActivity={props.setOpenActivity}
                setOpenShareContent={props.setOpenShareContent}
                setIsOpenPlaylist={props.setIsOpenPlaylist}
                onDataFetch={updateListCallback}
                sort={sectionSorts.get(section.code)}
                showDivider={props.showDivider}
                {...(props.subCategoryProps[section.code] || {})}
              />
            </>
          </TabPanel>
        ))}
      </Box>
      <Box
        display={{ xs: "flex", sm: "none" }}
        style={{ flexDirection: "column", height: "10rem", justifyContent: "space-between" }}
      >
        {getTabs().map((tab) => (
          <CustomButton
            title={tab.label}
            handleClick={() => handleCustomButtonClick(tab.tabId)}
            label={tab.label}
          />
        ))}
      </Box>
      <hr
        style={{
          color: theme.palette.grey["300"],
          width: "100vw",
          marginLeft: isMobile ? "-25px" : "-70px",
        }}
      />
    </>
  );
};

const TileView = (props: any): React.ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [contentCount, setContentCount] = useState<number | undefined>();
  const { t } = useTranslation();

  const updateListCallback = useCallback(
    (list: any, contentCount: number | undefined, code: string): void => {
      setContentCount(contentCount);
    },
    []
  );

  const handleViewAll = (): void => {
    dispatch({
      type: "subheader/title",
      payload: CategoryCode.Required,
    });
    navigate(`/catalog/${CategoryCode.Required}`);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent={"space-between"}
        marginTop={{ sm: "25px" }}
      >
        <LearningCarouselHeader
          title={props.title}
          showViewAll={false}
          handleViewAll={handleViewAll}
          showHeaderCount
          showHeaderWithZeroCount
          contentCount={contentCount}
        />
        {contentCount > 0 && (
          <CustomButton
            title={props.title}
            handleClick={handleViewAll}
            label={t("view_all_button_text")}
          />
        )}
      </Box>
      <Box display={{ xs: "none", sm: "flex" }}>
        <LearningCarousel
          key={CategoryCode.Required}
          code={CategoryCode.Required}
          onDataFetch={updateListCallback}
          selectTileHandler={props.selectTileHandler}
          setOpenActivity={props.setOpenActivity}
          setOpenShareContent={props.setOpenShareContent}
          setIsOpenPlaylist={props.setIsOpenPlaylist}
        />
      </Box>
    </>
  );
};

const CustomButton = (props: any): React.ReactElement => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const focusClass = useFocusStyles();

  return (
    <Button
      style={{
        width: `${isSmallScreen ? "100%" : "116px"}`,
        height: "38px",
        border: `1px solid ${
          portalSettingsManager.buttonColors?.normal?.active?.border || theme.palette.grey["A100"]
        }`,
        borderRadius: 0,
        color:
          portalSettingsManager.buttonColors?.normal?.active?.text || theme.palette.grey["800"],
        backgroundColor: portalSettingsManager.buttonColors?.normal?.active?.back,
        fontSize: "12px",
        fontWeight: 700,
        marginLeft: `${isSmallScreen ? "0px" : "20px"}`,
        marginRight: `${isMobile ? "0px" : "66px"}`,
      }}
      className={`${focusClass.focusItem} ${styles.focusOnly} ${focusClass.secondaryHover}`}
      onClick={props.handleClick}
      role="link"
      aria-label={props.title + " - " + props.label}
    >
      {props.label}
    </Button>
  );
};
